import { Component, Inject, Input, OnDestroy } from '@angular/core';

import { BehaviorSubject, Observable, Subject, forkJoin, of } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';

import { ParcMaintenance } from '../../../static-types/entities/parc-maintenance.entity';
import { ParcTechnologie } from '../../../static-types/entities/parc-technologie.entity';

import { SubjectRelay } from '../../../shared/commands/subject-relay.class';

import { AntenneService } from '../../structure/antenne/services/antenne.service';
import { ParcService } from '../../structure/parc/services/parc.service';
import { SousParcService } from '../../structure/sous-parc/services/sous-parc.service';
import { AntenneTLR } from '../../structure/antenne/entities/antenne-tlr.entity';
import { OuvrageService } from '../../structure/ouvrage/services/ouvrage.service';
import { SousParcTLR } from '../../structure/sous-parc/entities/sous-parc-tlr.entity';
import { OuvrageTLR } from '../../structure/ouvrage/entities/ouvrage-tlr.entity';
import { ParcTLR } from '../../structure/parc/entities/parc-tlr.entity';

import { SelectionPerimetre } from '../entities/selection-perimetre.entity';
import { ParcHyperviseur } from '../../structure/parc/entities/parc-hyperviseur.entity';

type AntenneDisplay = {
	entity: AntenneTLR,
	parcs: ParcDisplay[],
	selected: boolean,
};

type ParcDisplay = {
	entity: ParcTLR,
	ouvrages: OuvrageDisplay[],
	sousParcs: SousParcDisplay[],
	selected: boolean,
};

type SousParcDisplay = {
	entity: SousParcTLR,
	ouvrages: OuvrageDisplay[],
	selected: boolean,
};

type OuvrageDisplay = {
	entity: OuvrageTLR,
	selected: boolean,
}

@Component({
	selector: 'perimetre-selector',
	templateUrl: 'perimetre-selector.html',
	styleUrls: ['perimetre-selector.css'],
})
export class PerimetreSelectorComponent implements OnDestroy {
	resetMaintenanceCommands$ = new Subject<void>();
	resetTechnologieCommands$ = new Subject<void>();
	resetHyperviseurCommands$ = new Subject<void>();
	
	private antenneId$ = new BehaviorSubject<number | null>(null);
	
	// Valeurs presentees sur la vue
	antennes: AntenneDisplay[] = [];
	
	@Input() hyperviseurFilterEnabled: boolean = false;
	@Input() transfertPartiel: boolean = false;
	
	private technologiesSelectionnees: ParcTechnologie[] = [];
	private toutesTechnologiesSelectionnees: boolean = true;
	private hyperviseurFilterWasVisible = false;
	
	constructor(
		@Inject(AntenneService) antenneService: AntenneService,
		@Inject(ParcService) parcService: ParcService,
		@Inject(SousParcService) sousParcService: SousParcService,
		@Inject(OuvrageService) ouvrageService: OuvrageService,
	) {
		// Methodologie pour construire des OuvragesDisplay a partir d'une liste d'Ouvrage TLR
		let buildOuvragesDisplays = (ouvrages: OuvrageTLR[]) => {
			let ouvrageDisplays = ouvrages.map(
				ouvrage => ({
					entity: ouvrage,
					selected: true,
				} as OuvrageDisplay)
			);
			
			return ouvrageDisplays;
		}
		
		// Methodologie pour construire un SousParcDisplay a partir d'un SousParcTLR
		let buildSousParcDisplay = (sousParc: SousParcTLR, ouvrages: OuvrageTLR[]) => {
			let display = {
				entity: sousParc,
				ouvrages: buildOuvragesDisplays(ouvrages),
				selected: true,
			} as SousParcDisplay;
		
			return display;
		};
		
		// Methodologie pour construire un ParcDisplay a partir d'un ParcTLR
		let buildParcDisplay = (parc: ParcTLR, sousParcsMap: Map<number, SousParcTLR[]>, ouvragesMap: Map<number, OuvrageTLR[]>) => {
			// Grouper les ouvrages du parc
			let ouvrages = ouvragesMap.get(parc.id);
			let ouvragesBySousParc = new Map<number, OuvrageTLR[]>();
			let ouvragesSansSousParc: OuvrageTLR[] = [];
			if (ouvrages != null) {
				for (let ouvrage of ouvrages) {
					let sousParcId = ouvrage.sousParcId;
					if (sousParcId === null) {
						ouvragesSansSousParc.push(ouvrage);
					} else {
						let ouvragesDuSousParc = ouvragesBySousParc.get(sousParcId);
						if (ouvragesDuSousParc === undefined) {
							ouvragesDuSousParc = [] as OuvrageTLR[];
							ouvragesBySousParc.set(sousParcId, ouvragesDuSousParc);
						}
						ouvragesDuSousParc.push(ouvrage);
					}
				}
			}
			
			// Creer les SousParcDisplay
			let sousParcs = sousParcsMap.get(parc.id);
			let sousParcsDisplays = sousParcs === undefined ? [] : sousParcs.map(
				(sousParc) => {
					let ouvrages = ouvragesBySousParc.get(sousParc.id);
					if (ouvrages === undefined) {
						ouvrages = [];
					}
					
					return buildSousParcDisplay(sousParc, ouvrages);
				}
			);
			
			ouvragesSansSousParc.sort((o1, o2) => o1.nom.localeCompare(o2.nom));
			
			// Creer l'objet
			let display = {
				entity: parc,
				ouvrages: buildOuvragesDisplays(ouvragesSansSousParc),
				sousParcs: sousParcsDisplays,
				selected: true,
			} as ParcDisplay;

			return display;
		};
		
		// Methode pour creer un AntenneDisplay a partir d'un AntenneTLR
		let buildAntenneDisplay = (antenne: AntenneTLR, parcs: ParcDisplay[]) => {
			parcs.sort((pd1, pd2) => pd1.entity.code.localeCompare(pd2.entity.code));
			
			let display = {
				entity: antenne,
				parcs: parcs,
				selected: true,
			} as AntenneDisplay;

			return display;
		}

		// Methode de creation de map utilisee plus bas
		/*
		NB: cette methode servait a creer des map de parcs par maintenance et technologie, mais au final elles ont perdu leur utilite pour le moment
		on la garde en reserve au cas ou elles redeviendraient utiles
		
		let buildParcDisplayMap = (parcs: ParcDisplay[], keyGetter: (parc: ParcTLR) => string | null) => {
			let map = new Map<string, ParcDisplay[]>();
			for (let parc of parcs) {
				let key = keyGetter(parc.entity);
				if (key !== null && key !== undefined) {
					let list = map.get(key);
					if (list === undefined) {
						list = [] as ParcDisplay[];
						map.set(key, list);
					}
					list.push(parc);
				}
			}
			
			return map;
		}
		*/
		
		// Chargement des doneees de l'antenne
		let data$ = this.antenneId$.pipe(
			tap(
				(_antenneId) => {
					this.resetMaintenanceCommands$.next();
					this.resetTechnologieCommands$.next();
					this.resetHyperviseurCommands$.next();
				}
			),
			mergeMap(
				(antenneId) => {
					if (antenneId === null) {
						return of(null);
					}
					
					let antenne$ = antenneService.getAntenneTLRById(antenneId);
					let parcs$ = parcService.getParcTLRByAntenne(antenneId);
					let sousParcsMap$ = sousParcService.getSousParcTLRMapByParc();
					let ouvragesMap$ = ouvrageService.getOuvrageTLRMapByParc();
					
					let data$ = forkJoin({
						antenne: antenne$,
						parcs: parcs$,
						sousParcsMap: sousParcsMap$,
						ouvragesMap: ouvragesMap$,
					});
					
					return data$;
				}
			)
		);
		
		// Creation de la structure d'affichage
		let antenneDisplay$ = data$.pipe(
			map(
				(data) => {
					if (data === null) {
						return null;
					}
					
					let { antenne, parcs, sousParcsMap, ouvragesMap } = data;
					
					let parcsDisplays = parcs.filter( p => p.actif ).map(
						parc => buildParcDisplay(parc, sousParcsMap, ouvragesMap)
					);

					let antenneDisplay = buildAntenneDisplay(antenne, parcsDisplays);
					
					return antenneDisplay;
				}
			) 
		);
		
		// Binding des informations sur la vue
		antenneDisplay$.subscribe(
			antenne => {
				this.antennes = antenne ? [antenne] : [];
			}
		)

		// Gere les demande de recuperation de donnees venant du parent
		this.dataExtraction$.subscribe(
			(handler) => {
				let perimetre = {
					antennes: [],
					parcs: [],
					sousParcs: [],
					ouvrages: [],	
				} as SelectionPerimetre;
				
				for (let antenne of this.antennes) {
					if (antenne.selected) {
						perimetre.antennes.push(antenne.entity.id);
					} else {
						for (let parc of antenne.parcs) {
							if (parc.selected) {
								perimetre.parcs.push(parc.entity.id);
							} else {
								for (let ouvrage of parc.ouvrages) {
									if (ouvrage.selected) {
										perimetre.ouvrages.push(ouvrage.entity.id);
									}
								}
								
								for (let sousParc of parc.sousParcs) {
									if (sousParc.selected) {
										perimetre.sousParcs.push(sousParc.entity.id);
									} else {
										for (let ouvrage of sousParc.ouvrages) {
											if (ouvrage.selected) {
												perimetre.ouvrages.push(ouvrage.entity.id);
											}
										}
									}
								}
							}
						}
					}
				}
				handler(perimetre);
			}
		);
	}

	@Input() set antenneId(value: number | null) {
		if (value !== undefined) {
			this.antenneId$.next(value);
		}
	};

	private dataExtraction$ = new SubjectRelay<(p: SelectionPerimetre) => void>();
	@Input() set dataExtraction(obs: Observable<(p: SelectionPerimetre) => void>) {
		this.dataExtraction$.registerSource(obs);
	};
	
	get afficherFiltreHyperviseur(): boolean {
		return this.hyperviseurFilterEnabled
			&& this.transfertPartiel
			&& !this.toutesTechnologiesSelectionnees
			&& this.technologiesSelectionnees.length === 1
			&& this.technologiesSelectionnees[0].label === 'Solaire';
	}

	private applyParcDeselection(condition: (parc: ParcDisplay) => boolean) {
		for (let antenne of this.antennes) {
			let someDeselected = false;
			for (let parc of antenne.parcs) {
				if (condition(parc)) {
					this.changeParcSelectionInData(parc, false);
					someDeselected = true;
				}
			}
			if (someDeselected) {
				antenne.selected = false;
			}
		}
	}
	
	private syncHyperviseurFilterVisibility() {  
		const visible = this.afficherFiltreHyperviseur;
		if (this.hyperviseurFilterWasVisible && !visible) {
			this.resetHyperviseurCommands$.next();
			for (const tech of this.technologiesSelectionnees) {
				this.applyParcSelection(parc => parc.entity.energie === tech.label);
			}
		}
		this.hyperviseurFilterWasVisible = visible;
	}
	
	onTechnologieSelectionChange([entities, allSelected]: [ParcTechnologie[], boolean]) {
		this.technologiesSelectionnees = entities;
		this.toutesTechnologiesSelectionnees = allSelected;
		this.syncHyperviseurFilterVisibility();
	}
	
	hyperviseurDeselected(hyperviseur: ParcHyperviseur) {
		this.applyParcDeselection(
			parc => parc.entity.hyperviseur === hyperviseur.label
		);
	}
	
	maintenanceDeselected(maintenance: ParcMaintenance) {
		this.applyParcDeselection(
			parc => parc.entity.type === maintenance.label
		);
	}

	technologieDeselected(technologie: ParcTechnologie)	{
		this.applyParcDeselection(
			parc => parc.entity.energie === technologie.label
		);
	}
	
	hyperviseurSelected(hyperviseur: ParcHyperviseur) {
		this.applyParcSelection(
			parc => parc.entity.hyperviseur === hyperviseur.label
		);
	}

	private applyParcSelection(condition: (parc: ParcDisplay) => boolean) {
		for (let antenne of this.antennes) {
			let allSelected = true;
			for (let parc of antenne.parcs) {
				if (condition(parc)) {
					this.changeParcSelectionInData(parc, true);
				} else {
					allSelected = allSelected && parc.selected;
				}
			}
			if (allSelected) {
				antenne.selected = true;
			}
		}
	}
	
	maintenanceSelected(maintenance: ParcMaintenance) {
		this.applyParcSelection(
			parc => parc.entity.type === maintenance.label
		);
	}

	technologieSelected(technologie: ParcTechnologie) {
		this.applyParcSelection(
			parc => parc.entity.energie === technologie.label
		);
	}
	
	onAntenneChangedByUser(antenne: AntenneDisplay, selected: boolean) {
		if (antenne.selected !== selected) {
			antenne.selected = selected;
			
			for (let parc of antenne.parcs) {
				parc.selected = selected;
				for (let ouvrage of parc.ouvrages) {
					ouvrage.selected = selected;
				}
				for (let sousParc of parc.sousParcs) {
					sousParc.selected = selected;
					for (let ouvrage of sousParc.ouvrages) {
						ouvrage.selected = selected;
					}
				}
			}
		}
	}
	
	onParcChangedByUser(antenne: AntenneDisplay, parc: ParcDisplay, selected: boolean) {
		if (parc.selected !== selected) {
			this.changeParcSelectionInData(parc, selected);
			antenne.selected = antenne.parcs.every($ => $.selected);
		}
	}
	
	// Change l'etat de selection d'un parc et de tous ses sous-structures
	// NB: il faut cependant mettre a jour la selection de l'antenne a part
	private changeParcSelectionInData(parc: ParcDisplay, selected: boolean) {
		parc.selected = selected;
		for (let ouvrage of parc.ouvrages) {
			ouvrage.selected = selected;
		}
		for (let sousParc of parc.sousParcs) {
			sousParc.selected = selected;
			for (let ouvrage of sousParc.ouvrages) {
				ouvrage.selected = selected;
			}
		}
	}

	onSousParcChangedByUser(antenne: AntenneDisplay, parc: ParcDisplay, sousParc: SousParcDisplay, selected: boolean) {
		if (sousParc.selected !== selected) {
			sousParc.selected = selected;
			
			for (let ouvrage of sousParc.ouvrages) {
				ouvrage.selected = selected;
			}
			
			parc.selected = parc.sousParcs.every($ => $.selected) && parc.ouvrages.every($ => $.selected);
			antenne.selected = antenne.parcs.every($ => $.selected);
		}
	}

	onOuvrageChangedByUser(antenne: AntenneDisplay, parc: ParcDisplay, sousParc: SousParcDisplay | null, ouvrage: OuvrageDisplay, selected: boolean) {
		if (ouvrage.selected !== selected) {
			ouvrage.selected = selected;
			
			if (sousParc !== null) {
				sousParc.selected = sousParc.ouvrages.every($ => $.selected);
			}
			parc.selected = parc.sousParcs.every($ => $.selected) && parc.ouvrages.every($ => $.selected);
			antenne.selected = antenne.parcs.every($ => $.selected);
		}
	}

	ngOnDestroy() {
		this.dataExtraction$?.unsubscribe();
	}
}